import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Login from '@/pages/auth/Login';
import Register from '@/pages/auth/Register';
import ForgotPassword from '@/pages/auth/ForgotPassword';
import ResetPassword from '@/pages/auth/ResetPassword';
import VerifyEmail from '@/pages/auth/VerifyEmail';
import ExternalRedirect from './ExternalRedirect';
import PrivateRoute from './PrivateRoute';
import MainLayout from '@/layouts/MainLayout';
import MultiStrikePage from '@/pages/multi-strikes';

const Subscriptions = lazy(() => import('@/pages/settings/Subscriptions'));
const Refer = lazy(() => import('@/pages/settings/referral'));
const Profile = lazy(() => import('@/pages/settings/profile'));
const AdvanceChart = lazy(() => import('@/pages/advance-chart'));
const MultiStraddlePage = lazy(() => import('@/pages/multi-straddle'));
const CustomStranglePage = lazy(() => import('@/pages/custom-strangle'));
const DynamicAtmStraddle = lazy(() => import('@/pages/dynamic-atm-straddle'));
const StraddleTable = lazy(() => import('@/pages/straddle-table'));
const StraddleChart = lazy(() => import('@/pages/straddle-chart'));
const StrangleTable = lazy(() => import('@/pages/strangle-table'));
const StrangleChart = lazy(() => import('@/pages/strangle-chart'));
const StraddleAdv = lazy(() => import('@/pages/straddle-adv'));
const StrangleAdv = lazy(() => import('@/pages/strangle-adv'));
const BrokerLogin = lazy(() => import('@/pages/broker-login'));
const BrokerSetup = lazy(() => import('@/pages/broker-login/BrokerSetup'));
const OIAnalysis = lazy(() => import('@/pages/oi-analysis'));
const OptionsAnalysisTable = lazy(() => import('@/pages/options-analysis/table'));
const OptionsAnalysisCharts = lazy(() => import('@/pages/options-analysis/charts'));
const DynamicOptionsAnalysis = lazy(() => import('@/pages/dynamic-options-analysis'));
const HighsLowsHeatmap = lazy(() => import('@/pages/highs-lows-heatmap'));
const OptionOI = lazy(() => import('@/pages/option-oi'));
const OptionChain = lazy(() => import('@/pages/option-chain'));
const MarketMakerAnalysis = lazy(() => import('@/pages/market-maker-analysis'));
const VolatalityAnalysis = lazy(() => import('@/pages/volatility-analysis'));
const AdvanceDecline = lazy(() => import('@/pages/advance-decline'));
const MarketMovers = lazy(() => import('@/pages/market-movers'));
const FutureDashboard = lazy(() => import('@/pages/futures-dashboard'));
const FutSpotChart = lazy(() => import('@/pages/fut-spot-chart'));
const Seasonality = lazy(() => import('@/pages/seasonality'));
const IndexTop10 = lazy(() => import('@/pages/index-top-10'));
const TradeFinder = lazy(() => import('@/pages/trade-finder'));
const PreMarket = lazy(() => import('@/pages/pre-market'));
const QuickCharts = lazy(() => import('@/pages/quick-charts'));
const OptionAction = lazy(() => import('@/pages/option-action'));
const PCROITrend = lazy(() => import('@/pages/pcr-oi-trend'));
const OptionsPremium = lazy(() => import('@/pages/options-premium'));
const CashMarketActivity = lazy(() => import('@/pages/cash-market-activity'));
const FiiIndexActivity = lazy(() => import('@/pages/fii-index-activity'));
const ParticipantOI = lazy(() => import('@/pages/participant-oi'));
const MultiStraddle = lazy(() => import('@/pages/multi-straddle'));
const CustomStrangle = lazy(() => import('@/pages/custom-strangle'));
const AtmStraddlePrices = lazy(() => import('@/pages/atm-straddle-prices'));
const NseSectorConstituents = lazy(() => import('@/pages/nse-sector-constituents'));
const HighsLows = lazy(() => import('@/pages/highs-lows'));
const MwplBanList = lazy(() => import('@/pages/mwpl-ban-list'));
const HolidayList = lazy(() => import('@/pages/holiday-list'));
const OptionChainCharts = lazy(() => import('@/pages/option-chain-charts'));

const PageLoader = () => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <div className="page-spinner" />
    </div>
);

const BASE = import.meta.env.VITE_SITE_URL;

const AppRouter = () => (
    <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
            <Routes>
                <Route path="/authentication/login" element={<Login />} />
                <Route path="/login" element={<Navigate to="/authentication/login" replace />} />
                <Route path="/authentication/register" element={<Register />} />
                <Route path="/authentication/forgot-password" element={<ForgotPassword />} />
                <Route path="/authentication/reset-password" element={<ResetPassword />} />
                <Route path="/authentication/verify-email" element={<VerifyEmail />} />

                <Route path="/pricing" element={<ExternalRedirect to={`${BASE}/pricing`} />} />
                <Route path="/contact-us" element={<ExternalRedirect to={`${BASE}/contact-us`} />} />
                <Route path="/privacy-policy" element={<ExternalRedirect to={`${BASE}/privacy-policy`} />} />
                <Route path="/terms-condition" element={<ExternalRedirect to={`${BASE}/terms-condition`} />} />
                <Route path="/refund-policy" element={<ExternalRedirect to={`${BASE}/refund-policy`} />} />
                <Route path="/about-us" element={<ExternalRedirect to={`${BASE}/about-us`} />} />

                <Route path="/*" element={<PrivateRoute><MainLayout /></PrivateRoute>}>
                    <Route path="account/subscriptions" element={<Subscriptions />} />
                    <Route path="account/refer" element={<Refer />} />
                    <Route path="account/profile" element={<Profile />} />
                    <Route path="account/broker-login" element={<BrokerLogin />} />
                    <Route path="account/broker-setup" element={<BrokerSetup />} />
                    <Route path="pages/technical-analysis" element={<AdvanceChart />} />
                    <Route path="pages/dynamic-atm-straddle" element={<DynamicAtmStraddle />} />
                    <Route path="pages/straddle-table" element={<StraddleTable />} />
                    <Route path="pages/straddle-charts" element={<StraddleChart />} />
                    <Route path="pages/strangle-table" element={<StrangleTable />} />
                    <Route path="pages/strangle-charts" element={<StrangleChart />} />
                    <Route path="pages/straddle-advance-chart" element={<StraddleAdv />} />
                    <Route path="pages/strangle-advance-chart" element={<StrangleAdv />} />
                    <Route path="pages/multi-strikes/:mode" element={<MultiStrikePage />} />
                    <Route path="pages/multi-straddle" element={<MultiStraddlePage />} />
                    <Route path="pages/custom-strangle" element={<CustomStranglePage />} />
                    <Route path="pages/open-interest-analysis" element={<OIAnalysis />} />
                    <Route path="pages/options-analysis/table" element={<OptionsAnalysisTable />} />
                    <Route path="pages/options-analysis/charts" element={<OptionsAnalysisCharts />} />
                    <Route path="pages/dynamic-options-analysis" element={<DynamicOptionsAnalysis />} />
                    <Route path="pages/highs-lows-heatmap" element={<HighsLowsHeatmap />} />
                    <Route path="pages/option-oi" element={<OptionOI />} />
                    <Route path="pages/option-chain-charts" element={<OptionChainCharts />} />
                    <Route path="pages/option-chain" element={<OptionChain />} />
                    <Route path="pages/market-maker-analysis" element={<MarketMakerAnalysis />} />
                    <Route path="pages/volatality-analysis" element={<VolatalityAnalysis />} />
                    <Route path="pages/advance-decline" element={<AdvanceDecline />} />
                    <Route path="pages/market-movers" element={<MarketMovers />} />
                    <Route path="pages/futures-dashboard" element={<FutureDashboard />} />
                    <Route path="pages/fut-spot-chart" element={<FutSpotChart />} />
                    <Route path="pages/seasonality" element={<Seasonality />} />
                    <Route path="pages/top-constituents" element={<IndexTop10 />} />
                    <Route path="pages/trade-finder" element={<TradeFinder />} />
                    <Route path="pages/pre-market" element={<PreMarket />} />
                    <Route path="pages/quick-charts" element={<QuickCharts />} />
                    <Route path="pages/option-action" element={<OptionAction />} />
                    <Route path="pages/pcr-oi-trend" element={<PCROITrend />} />
                    <Route path="pages/options-premium" element={<OptionsPremium />} />
                    <Route path="pages/cash-market-activity" element={<CashMarketActivity />} />
                    <Route path="pages/fii-index-activity" element={<FiiIndexActivity />} />
                    <Route path="pages/participant-oi" element={<ParticipantOI />} />
                    <Route path="pages/multi-straddle" element={<MultiStraddle />} />
                    <Route path="pages/custom-strangle" element={<CustomStrangle />} />
                    <Route path="pages/atm-straddle-prices" element={<AtmStraddlePrices />} />
                    <Route path="pages/nse-sector-constituents" element={<NseSectorConstituents />} />
                    <Route path="pages/highs-lows" element={<HighsLows />} />
                    <Route path="pages/ban-list" element={<MwplBanList />} />
                    <Route path="pages/holiday-list" element={<HolidayList />} />
                </Route>
                {/* <Route path="/404" element={<NotFound />} />   */}
                <Route path="*" element={<Navigate to="/404" replace />} />
            </Routes>
        </Suspense>
    </BrowserRouter>
);

export default AppRouter;